/**
 * Module 'policy': rules that decide approvals and gate actions (architecture spec section 7).
 *
 * A rule matches a request by tool, action, pattern and model; every field it sets must match.
 * Precedence: project rules for the request's project, then org rules, then the built-in
 * defaults (section 7.1). Within one scope, deny beats ask beats allow.
 */
import type { ActionKind, Id, Ts } from '../index.ts';
import type { AgentKind } from './adapters.ts';
import type { LaunchAccountGrant } from './launch-accounts.ts';

export type PolicyScope = { kind: 'org' } | { kind: 'project'; projectId: Id };
export type PolicyDecision = 'allow' | 'ask' | 'deny';

export interface PolicyRule {
  id: Id;
  scope: PolicyScope;
  match: {
    /** Claude Code tool name ('Bash', 'Edit', 'mcp__*'), a glob, case-insensitive. */
    tool?: string;
    /** An Orgus action kind. */
    action?: ActionKind;
    /**
     * Glob over the command or path, e.g. 'npm test*', 'git push * main', 'src/**'.
     * `*` matches anything (spaces and slashes too), `?` one character. A trailing ':*'
     * (Claude Code style, 'npm run:*') matches the prefix alone or followed by arguments.
     */
    pattern?: string;
    model?: string;
  };
  decision: PolicyDecision;
  reason: string;
  createdBy: string;
  createdAt: Ts;
  /** Hits in the last 7 days. */
  hits?: number;
  /** Off rules are kept but never match. Default on. */
  enabled?: boolean;
  updatedBy?: string;
  updatedAt?: Ts;
}

export interface PolicyRequest {
  projectId: Id | null;
  tool?: string;
  action?: ActionKind;
  /** The command or path the pattern is matched against. */
  subject?: string;
  model?: string;
  /** The session's working folder: file paths outside it count as "outside the worktree". */
  cwd?: string;
}

export interface PolicyResult {
  /** The person whose rule or autonomy setting supplied this decision. */
  onBehalfOf?: string;
  decision: PolicyDecision;
  /** A rule id, or 'default:<name>' for a built-in default. */
  ruleId: Id | null;
  reason: string;
}

/** One change to the rule set: every create, update and delete is versioned. */
export interface PolicyChange {
  version: number;
  at: Ts;
  by: string;
  kind: PolicyChangeKind;
  /** The rule a rule change is about; '' for settings, freeze and rollback changes. */
  ruleId: Id;
  before?: PolicyRule;
  after?: PolicyRule;
  /** For 'settings', 'freeze' and 'rollback': the settings and freezes before and after. */
  configBefore?: PolicyConfig;
  configAfter?: PolicyConfig;
  /** For 'rollback': the whole rule set after the change, and the version it restored. */
  rules?: PolicyRule[];
  rollbackOf?: number;
  /** One line: "Allow: Bash npm test:* (project)", "Rolled back to version 4". */
  summary?: string;
}

/** Every rule change, settings change, freeze change and rollback is one version. */
export type PolicyChangeKind = 'create' | 'update' | 'delete' | 'settings' | 'freeze' | 'rollback';

/** state.ext.policy */
export interface PolicyState {
  rules: PolicyRule[];
  version: number;
  /** The built-in defaults, lowest precedence (read-only). */
  defaults?: PolicyRule[];
  /** Hits in the last 7 days per rule id, defaults included. */
  hits?: Record<string, number>;
  updatedAt?: Ts;
  /** Launch settings and landing rules per layer, and freeze windows (policy v2). */
  config?: PolicyConfig;
  /** Freeze windows in force now. */
  frozen?: ActiveFreeze[];
  /** Model switches seen in sessions, newest first (last 50). */
  modelSwitches?: ModelSwitch[];
}

/** Body of POST /api/policy/rules and PUT /api/policy/rules/:id. */
export interface PolicyRuleInput {
  scope: PolicyScope;
  match: PolicyRule['match'];
  decision: PolicyDecision;
  reason?: string;
  enabled?: boolean;
}

/** Body of POST /api/policy/simulate: a candidate rule set, or one rule added or replaced. */
export interface PolicySimulationRequest {
  /** The whole candidate rule set. Default: the current rules. */
  rules?: PolicyRule[];
  /** One rule to add (or replace, when its id exists) on top of the rule set. */
  rule?: PolicyRuleInput & { id?: Id };
  /** Rule ids to leave out. */
  without?: Id[];
  /** How far back to replay, default 7. */
  days?: number;
  /** Replay against the rules of this earlier version instead (what a rollback would do). */
  version?: number;
}

export interface PolicySimulationChange {
  at: Ts;
  title: string;
  tool?: string;
  action?: ActionKind;
  subject?: string;
  projectId: Id | null;
  before: PolicyDecision;
  after: PolicyDecision;
  ruleId: Id | null;
}

export interface PolicySimulationResult {
  from: Ts;
  to: Ts;
  /** Requests replayed from the audit log. */
  total: number;
  before: Record<PolicyDecision, number>;
  after: Record<PolicyDecision, number>;
  /** Requests whose decision would change, newest first (at most 200). */
  changed: PolicySimulationChange[];
  changedCount: number;
  /** Asks under the candidate rules, by the rule that asks (ADR 0101). Older hubs omit it. */
  askBy?: Record<string, number>;
  /**
   * Of those, the asks a person would see whoever answers the rest: what leaves the machine
   * (default:outward) and ask rules people wrote. The other default asks Orgus settles when it
   * answers (ADR 0074). Older hubs omit it.
   */
  personAsks?: number;
}

/** What other hub modules may ask the policy module: ctx.ext<PolicyApi>('policy'). */
export interface PolicyApi {
  evaluate(req: PolicyRequest): PolicyResult;
  /** Add a rule (audited, versioned). Used by approvals for "Make it a rule". */
  addRule(input: PolicyRuleInput, actor: string): PolicyRule;
  /** Count a decision against the rule that made it. */
  recordHit(ruleId: Id | null, at?: Ts): void;
  /** The launch record of a session Orgus launched (ADR 0088: which account it ran on), or null. */
  launchOf?(sessionId: Id): LaunchRecord | null;
  /** What a session launched in this project would get, with where each value came from. */
  effective?(projectId: Id | null, session?: { model?: string; permissionMode?: string }): EffectiveLaunch;
  /** May an item land in this project now (freeze windows, landing rules). */
  landing?(projectId: Id | null, at?: Ts): LandingDecision;
  /** The freeze window that stops `block` in this project now (the one that ends last), or null. */
  frozen?(projectId: Id | null, block: FreezeBlock, at?: Ts): ActiveFreeze | null;
  /** ADR 0070: the project's autonomy (over the org's, over 'ask'). Orgus Watch's remedies run only on 'full' (ORGU-92). */
  autonomy?(projectId: Id | null): ApprovalAutonomy;
  /**
   * An in-console terminal launch the hub authorised (module term): leaves the ticket its grant
   * needs, as session.start does, so its launch account applies (ADR 0071). The ticket matches
   * only a launch request with `pty`. Returns the project the launch was placed in.
   */
  ticketPtyLaunch?(t: { machineId: Id; cwd: string | null; checkoutId?: Id; by: string; launchAccountId?: string | null }): { projectId: Id | null };
  /**
   * An assistant reply or AI job the hub is sending to a machine (ADR 0107 amendment, 24 Oct 2026):
   * leaves a ticket for exactly that run id, so the launch request the machine sends with
   * `runOf` set to it gets the launch account (named, else the workspace default) and its key.
   */
  ticketRunLaunch?(t: { machineId: Id; runId: Id; by: string; launchAccountId: string | null }): void;
  /** R-FN-6: how long a launched session in this project may sit idle before Orgus closes it (ms; 0: never). */
  idleCloseMs?(projectId: Id | null): number;
}

// ---------------------------------------------------------------------------
// Policy v2: launch settings for sessions Orgus starts, landing rules, freeze windows,
// versions with diff and rollback (architecture doc 7 and 11.8, cost doc 9).
// ---------------------------------------------------------------------------

/** Who must review before an item lands. */
export type LandingReviewer = 'none' | 'human' | 'agent' | 'human-or-agent';

/**
 * One layer of settings (org or project). Unset fields inherit from the layer below:
 * session over project over org over the built-in defaults.
 */
export interface PolicySettings {
  /** The model a launched session runs when the person starting it picks none. */
  defaultModel?: string;
  /** Models a launched session may run (globs: 'opus', 'claude-sonnet-*'). Unset: any. */
  allowedModels?: string[];
  /** Claude Code's permission mode for launched sessions (default, acceptEdits, plan, ...). */
  permissionMode?: string;
  /** Refuse bypassPermissions in launched sessions. */
  disableBypassPermissions?: boolean;
  /**
   * ADR 0070. full: what only the floor or a built-in default would ask a person (inline code,
   * protected config, network, outside the session folder) is allowed at once, so nothing waits;
   * an ask rule a person wrote still asks, and a deny (the Orgus home) still denies. ask: as before.
   */
  autonomy?: ApprovalAutonomy;
  /** Names of secrets leased to each launched session as environment variables. */
  secrets?: string[];
  /** Plain environment variables for launched sessions. Never secrets: these go in the settings file. */
  env?: Record<string, string>;
  /** Checks that must pass before an item lands ('tests', 'lint', 'review'). */
  requiredChecks?: string[];
  reviewer?: LandingReviewer;
  /**
   * Agent kinds a mission in scope may run (ADR 0049 decision 5): kind tokens such as
   * claude-code, codex-acp, gemini-cli or a kind the ACP Registry adds. Unset: every kind. The
   * project layer replaces the org layer. Additive, 19 Oct 2026.
   */
  allowedAgentKinds?: string[];
  /** ORGU-244: review pull requests opened on this repository. */
  prReview?: PrReviewSettings;
  /**
   * R-FN-6: minutes a session Orgus launched may sit idle (its turn ended, nothing asked, nothing
   * queued) before Orgus closes it, keeping it resumable. 0: never. Unset: DEFAULT_IDLE_CLOSE_MINUTES.
   */
  idleCloseMinutes?: number;
}

/** R-FN-6: the idle close when no layer sets idleCloseMinutes, and the largest a layer may set. */
export const DEFAULT_IDLE_CLOSE_MINUTES = 10;
export const IDLE_CLOSE_MAX_MINUTES = 24 * 60;

export interface PrReviewSettings {
  enabled?: boolean;
  dailyBudgetUsd?: number | null;
}

/** ADR 0070: who answers what only the floor or a built-in default would ask. */
export type ApprovalAutonomy = 'full' | 'ask';
export const APPROVAL_AUTONOMY_LEVELS: readonly ApprovalAutonomy[] = ['full', 'ask'];
export const APPROVAL_AUTONOMY_LABEL: Record<ApprovalAutonomy, string> = { full: 'Full autonomy', ask: 'Ask people' };

export type PolicySettingKey = keyof PolicySettings;
export const POLICY_SETTING_KEYS: readonly PolicySettingKey[] = [
  'defaultModel',
  'allowedModels',
  'permissionMode',
  'disableBypassPermissions',
  'autonomy',
  'secrets',
  'env',
  'requiredChecks',
  'reviewer',
  'allowedAgentKinds',
  'prReview',
  'idleCloseMinutes',
];

/** What a freeze window stops. */
export type FreezeBlock = 'landing' | 'deploy' | 'session.start';
export const FREEZE_BLOCKS: readonly FreezeBlock[] = ['landing', 'deploy', 'session.start'];

export interface FreezeWindow {
  id: Id;
  name: string;
  scope: PolicyScope;
  /** once: from..to. weekly: on `days` (0 Sunday to 6 Saturday) between start and end. */
  kind: 'once' | 'weekly';
  from?: Ts;
  to?: Ts;
  days?: number[];
  /** 'HH:MM', 24-hour. An end at or before the start runs past midnight. */
  start?: string;
  end?: string;
  /** Minutes east of UTC that the weekly times are in (the editor's offset when saved). */
  utcOffsetMinutes?: number;
  blocks: FreezeBlock[];
  reason?: string;
  /** Off windows are kept but never apply. Default on. */
  enabled?: boolean;
  createdBy?: string;
  createdAt?: Ts;
}

/** The settings layers and freeze windows. Versioned together with the rules. */
export interface PolicyConfig {
  org: PolicySettings;
  projects: Record<Id, PolicySettings>;
  freezes: FreezeWindow[];
}

/** A freeze in force now, and when it ends. */
export interface ActiveFreeze {
  window: FreezeWindow;
  until: Ts;
}

/** Where a value came from, lowest to highest precedence. */
export type SettingSource = 'default' | 'org' | 'project' | 'session';

/** One resolved setting: its value, the layer that set it, and the lower layers it replaced. */
export interface EffectiveSetting {
  key: PolicySettingKey | 'model';
  label: string;
  value: unknown;
  source: SettingSource;
  overridden: { source: SettingSource; value: unknown }[];
  note?: string;
}

/** One Claude Code permission rule generated from an Orgus rule. */
export interface GeneratedPermission {
  /** 'Bash(npm test *)', 'Edit(src/**)', 'mcp__github'. */
  rule: string;
  list: 'allow' | 'ask' | 'deny';
  source: 'org' | 'project';
  ruleId: Id;
}

/** Everything a launched session gets from policy, and where each value came from. */
export interface EffectiveLaunch {
  projectId: Id | null;
  /** The model the session runs (null: Claude Code's own default). */
  model: string | null;
  settings: EffectiveSetting[];
  permissions: GeneratedPermission[];
  /** Orgus rules Claude Code's rule syntax cannot express; the hooks still enforce them. */
  hookOnly: { ruleId: Id; why: string }[];
  /** The Claude Code settings Orgus passes with --settings (the machine adds its hooks). */
  claude: Record<string, unknown>;
  /** Secret names leased to the session (values never appear here). */
  secrets: string[];
  /** Why the launch must be refused (a model outside the allowed list, a freeze). Empty: fine. */
  violations: string[];
}

/** One launch and what it got. Kept 30 days for "where did this value come from". */
export interface LaunchRecord {
  runId: string;
  machineId: Id;
  projectId: Id | null;
  sessionId: Id | null;
  cwd: string;
  mode: 'headless' | 'terminal';
  at: Ts;
  policyVersion: number;
  effective: EffectiveLaunch;
  leaseId: Id | null;
  endedAt?: Ts;
  /** The launch account it ran on (ADR 0071), when one applied. */
  launchAccountId?: string;
}

/** A session's model changed (seen in its transcript), checked against model policy. */
export interface ModelSwitch {
  at: Ts;
  sessionId: Id;
  machineId: Id;
  projectId: Id | null;
  from: string | null;
  to: string;
  allowed: boolean;
  /** True for a session Orgus started. */
  launched: boolean;
  reason?: string;
}

/** A version in the list: who changed what, when. */
export interface PolicyVersionInfo {
  version: number;
  at: Ts;
  by: string;
  kind: PolicyChangeKind;
  summary: string;
  rollbackOf?: number;
}

/** The whole policy at one version. */
export interface PolicySnapshot {
  version: number;
  rules: PolicyRule[];
  config: PolicyConfig;
}

export interface PolicyDiffEntry {
  area: 'rule' | 'setting' | 'freeze';
  change: 'added' | 'removed' | 'changed';
  /** A rule id, '<org|projectId>.<key>' for a setting, a freeze id. */
  id: string;
  label: string;
  /** null for the org layer and org rules, else the project id. */
  projectId: Id | null;
  before?: unknown;
  after?: unknown;
}

export interface PolicyDiff {
  from: number;
  to: number;
  entries: PolicyDiffEntry[];
}

/** May an item land now: freezes and the landing rules in force. */
export interface LandingDecision {
  allowed: boolean;
  frozen: ActiveFreeze | null;
  requiredChecks: string[];
  reviewer: LandingReviewer;
  reason: string;
}

/** Body of PUT /api/policy/settings: one layer, replaced whole. */
export interface PolicySettingsInput {
  scope: PolicyScope;
  settings: PolicySettings;
}

/** Body of POST /api/policy/freezes and PUT /api/policy/freezes/:id. */
export type FreezeWindowInput = Omit<FreezeWindow, 'id' | 'createdBy' | 'createdAt'>;

// Agent module 'policy' <-> hub module 'policy' (ExtMessage with ext 'policy').

/** agent -> hub, type 'launch', id = runId: a session.start is about to run Claude Code (or `agentKind`). */
export interface LaunchRequest {
  runId: string;
  cwd: string;
  mode: 'headless' | 'terminal';
  projectId: Id | null;
  model?: string;
  permissionMode?: string;
  /** The harness when it is not Claude Code (absent: Claude Code). Its launch gets no Claude Code settings file. */
  agentKind?: AgentKind;
  /**
   * mode 'terminal' in an in-console terminal (a PTY the agent spawns, module term), not a
   * terminal window: the agent sets its environment, so a launch account applies (ADR 0071).
   */
  pty?: boolean;
  /**
   * An assistant reply or AI job (not a session): the hub's run id it ticketed (ticketRunLaunch).
   * Only the launch account is granted, never policy's secrets or settings.
   */
  runOf?: string;
}

/** hub -> agent, type 'launch.result', same id. Secret values travel only in `env`. */
export interface LaunchGrant {
  /** Claude Code settings to merge into the launch's --settings file (no secrets). */
  settings: Record<string, unknown>;
  /** Leased secrets, NAME to value: kept in the agent's memory and given to the process only. */
  env: Record<string, string>;
  leaseId: Id | null;
  /** Lines for the run's log: "Leased 2 secrets: A, B". */
  notes: string[];
  /** Set when policy refuses the launch. */
  refused?: string;
  /**
   * The launch account it runs on (ADR 0071), with its leased secret: absent when the launch named
   * none and its project has no default (today's behaviour). Agents that do not know it ignore it.
   */
  account?: LaunchAccountGrant;
}

/** hub -> agent, type 'redact': secret values to mask in everything the agent sends. Memory only. */
export interface RedactionPush {
  values: { name: string; value: string }[];
  /** The workspace data setting (DataSharing.content); absent from hubs older than WP-15. */
  content?: boolean;
}

// ---------------------------------------------------------------------------
// Data sent: "Send transcript and diff content" (WP-15; R-EN-3, R-EN-4)
// ---------------------------------------------------------------------------

/**
 * A workspace setting, stored by the hub's policy module and delivered to every agent. On (the
 * default) agents send what they send today. Off ("metadata only"), an agent leaves out the text
 * of transcripts and diffs from every message it sends, so prompts, replies and source code stay
 * on the machine; counts, paths, statuses, tokens and costs still flow.
 *
 *   GET  /api/policy/data            DataSharing
 *   PUT  /api/policy/data {content}  owner, admin; audited 'policy.data'
 *
 *   hub -> agent  ext policy 'data'    DataSharingPush, on connect and on every change
 *   hub -> agent  ext policy 'redact'  also carries `content` (the answer to 'redact.get')
 *
 * What an agent leaves out when `content` is false (agent-ts: agents/agent-ts/src/ext/policy/
 * content.ts; the Go agent mirrors it in internal/agent/ext_policy.go Outbound):
 *   - sessions in snapshot and patch: `title` becomes "Session in <folder>", `lastText`, each
 *     subagent's `description` and `lastText`, and each workflow agent's `lastText` become ''
 *   - feed events about sessions and subagents: the text after "Session started:" and the like
 *   - logs.chunk of a session (its timeline or a headless run's output): one line saying why
 *   - ext ai 'transcript.result': every entry's `text`
 *   - ext session-control 'result' events: `title` becomes the event kind, `detail` and `edit` go
 *   - ext diff 'result': every file's `hunks` (paths and line counts stay), with a `note`
 *   - ext discipline 'session': `title`
 * Not affected, because a person asked for them in that moment: approval requests (the person
 * deciding must see the command), terminals a person opens, and answers to the assistant and AI
 * jobs. An agent that has not heard from the hub yet keeps the last value it was told (agent-ts
 * keeps it in <home>/policy/data.json), and on before it was ever told.
 */
export const POLICY_DATA_PATH = '/api/policy/data';

export interface DataSharing {
  /** Send transcript and diff text. Default true. */
  content: boolean;
  updatedAt: Ts | null;
  updatedBy: string | null;
}

export const DATA_SHARING_DEFAULT: DataSharing = { content: true, updatedAt: null, updatedBy: null };

/** hub -> agent, ext policy 'data'. */
export interface DataSharingPush {
  content: boolean;
}

/** The line an agent sends in place of a session's transcript when content is off. */
export const CONTENT_OFF_NOTICE = 'Transcript content stays on this machine: this workspace sends metadata only (Settings, Data sent).';
